import { Stack } from '@mui/material';
import { first } from 'lodash';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/form/baseInputs/Button';
import { DesignerLayoutGrid } from '@/components/layout/DesignerLayoutGrid/DesignerLayoutGrid';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useModalState } from '@/hooks/useModalState';
import { useReportError } from '@/hooks/useReportError';
import { HouseholdPermissionAwareDisabledProvider } from '@/modules/authentication/context/HouseholdPermissionAwareDisabledProvider/HouseholdPermissionAwareDisabledProvider';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { FinalizeProposalModal } from '@/modules/gifting/proposal/designer/form/components/FinalizeProposalModal/FinalizeProposalModal';
import { useIRSConstants } from '@/modules/irs/useIRSConstants';
import {
  CharitableTrustDesignerStages,
  ROUTE_KEYS,
} from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { getNodes } from '@/utils/graphqlUtils';

import {
  useCharitableTrustDesignerContext,
  useIsCRT,
} from '../CharitableTrustDesignerContext';
import { useUpdateCharitableTrustProposalMutation } from '../graphql/CharitableTrustProposalMutations.generated';
import { DEFAULT_CHARITABLE_TRUST_DESIGNER_ANALYSIS_FORM } from './CharitableTrustDesignerAnalysis.constants';
import { CharitableTrustDesignerAnalysisForm } from './CharitableTrustDesignerAnalysis.types';
import {
  mapAnalysisDataToClientProfileUpdatePayload,
  mapAnalysisDataToMutationPayload,
  mapDataToAnalysisForm,
} from './CharitableTrustDesignerAnalysis.utils';
import { CharitableTrustDesignerAnalysisIllustration } from './CharitableTrustDesignerAnalysisIllustration';
import { CharitableTrustDesignerAnalysisTrustStructure } from './CharitableTrustDesignerAnalysisTrustStructure';
import {
  CharitableTrustDesignerAnalysisDataDocument,
  useCharitableTrustDesignerAnalysisDataQuery,
} from './graphql/CharitableTrustDesignerAnalysisData.generated';

interface CharitableTrustDesignerAnalysisProps {
  onFormAction?: (
    action: 'cancel' | CharitableTrustDesignerStages,
    crtId?: string
  ) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export function CharitableTrustDesignerAnalysis({
  onFormAction,
  onDirtyChange,
}: CharitableTrustDesignerAnalysisProps) {
  const navigate = useNavigate();
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();
  const { rate7520 } = useIRSConstants();
  const { proposalId, householdId, variant } =
    useCharitableTrustDesignerContext();
  const canEdit = useCanEditHouseholdData();

  const isCRT = useIsCRT();
  const [
    { isModalOpen: isShareModalOpen },
    { openModal: openShareModal, closeModal: closeShareModal },
  ] = useModalState();

  const formMethods = useForm<CharitableTrustDesignerAnalysisForm>({
    defaultValues: DEFAULT_CHARITABLE_TRUST_DESIGNER_ANALYSIS_FORM,
  });

  const {
    reset,
    formState: { isSubmitting: formIsSubmitting, isDirty },
    handleSubmit,
    getValues,
  } = formMethods;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const { data: loadedData, loading: loadingFormData } =
    useCharitableTrustDesignerAnalysisDataQuery({
      // necessary to update form after saving the modal
      // https://github.com/apollographql/apollo-client/issues/11306
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      variables: {
        proposalId,
      },
      onError: (error) => {
        showFeedback(
          'Could not load proposal data.  Please refresh the page and try again.'
        );
        reportError(`Caught error when loading ${proposalId}`, error);
      },
      onCompleted: (data) => {
        reset(mapDataToAnalysisForm(data, rate7520, getValues()));
      },
    });

  const [updateCharitableTrustProposalMutation] =
    useUpdateCharitableTrustProposalMutation({
      onError: (error) => {
        showFeedback('Could not update proposal data.  Please try again.');
        reportError(`Caught error when updating ${proposalId}`, error);
      },
      onCompleted: () => {
        showFeedback('Proposal updated', { variant: 'success' });
      },
    });

  const loadedProposal = first(getNodes(loadedData?.proposals));

  const handleSave = handleSubmit(async (formData) => {
    if (!loadedProposal) {
      showFeedback(
        'Could not load proposal data.  Please refresh the page and try again.'
      );
      reportError(
        `Tried to save charitable trust analysis for ${proposalId} with no loaded proposal`
      );
      return;
    }

    const input = mapAnalysisDataToMutationPayload({
      formData,
      loadedData,
      isCRT,
    });
    const donorProfiles = mapAnalysisDataToClientProfileUpdatePayload({
      formData,
      loadedData,
    });
    const mutationResult = await updateCharitableTrustProposalMutation({
      variables: {
        input,
        updateDonorProfiles: true,
        householdID: householdId,
        donorProfiles,
      },
      refetchQueries: [CharitableTrustDesignerAnalysisDataDocument],
    });

    if (onFormAction) {
      onFormAction(
        CharitableTrustDesignerStages.MODEL_SCENARIOS,
        mutationResult.data?.updateProposalV2.id
      );
      return;
    }

    openShareModal();
  });

  const isDisabled = formIsSubmitting || loadingFormData;

  return (
    <FormProvider {...formMethods}>
      <HouseholdPermissionAwareDisabledProvider forceDisabled={isDisabled}>
        <DesignerLayoutGrid
          LeftColumn={<CharitableTrustDesignerAnalysisTrustStructure />}
          RightColumn={<CharitableTrustDesignerAnalysisIllustration />}
          footerLeftAction={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (onFormAction) {
                  onFormAction('cancel');
                  return;
                }

                navigate(
                  getCompletePathFromRouteKey(
                    ROUTE_KEYS.HOUSEHOLD_DETAILS_PROPOSALS,
                    {
                      householdId,
                    }
                  )
                );
              }}
            >
              Cancel
            </Button>
          }
          footerRightAction={
            <>
              {!onFormAction && canEdit && (
                <FinalizeProposalModal<CharitableTrustDesignerAnalysisForm>
                  householdId={householdId}
                  proposalId={proposalId}
                  isOpen={isShareModalOpen}
                  onClose={closeShareModal}
                  onSave={handleSave}
                />
              )}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="transparent"
                  size="sm"
                  onClick={() => {
                    if (onFormAction) {
                      onFormAction(
                        CharitableTrustDesignerStages.MODEL_SCENARIOS
                      );
                      return;
                    }

                    navigate(
                      getCompletePathFromRouteKey(
                        ROUTE_KEYS.HOUSEHOLD_GIFT_CHARITABLE_TRUST_DESIGNER_BASIC_INFORMATION,
                        { householdId, proposalId, variant }
                      )
                    );
                  }}
                >
                  Previous
                </Button>
                {canEdit && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSave}
                    loading={formIsSubmitting}
                    disabled={loadingFormData || !loadedProposal}
                  >
                    {onFormAction ? 'Save and close' : 'Save & share'}
                  </Button>
                )}
              </Stack>
            </>
          }
        />
      </HouseholdPermissionAwareDisabledProvider>
    </FormProvider>
  );
}
